import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { 
  Users, UserPlus, Edit2, Trash2, Eye, Shield, 
  ChevronLeft, Search, X, Save, Calendar, Mail,
  Crown, User, Lock, Unlock
} from 'lucide-react';
import './AdminDashboard.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Zodiac display helper
const getZodiacDisplay = (zodiac) => {
  if (!zodiac) return null;
  return `${zodiac.chinese} ${zodiac.english}`;
};

// Format date helper
const formatDate = (dateStr) => {
  if (!dateStr) return 'Not set';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  } catch {
    return dateStr;
  }
};

export function AdminDashboard({ onBack }) {
  const { token, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch users');
      
      const data = await response.json();
      setUsers(data);
    } catch (error) {
      toast.error('Failed to load users');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Create user
  const handleCreateUser = async (userData) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(userData)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create user');
      }
      
      toast.success('User created successfully');
      setShowCreateModal(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Update user
  const handleUpdateUser = async (userId, userData) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(userData)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update user');
      }
      
      toast.success('User updated successfully');
      setShowEditModal(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Delete user
  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete "${userName}"?`)) {
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete user');
      }
      
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Toggle user active status
  const handleToggleStatus = async (user) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users/${user.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: !user.is_active })
      });
      
      if (!response.ok) throw new Error('Failed to update status');
      
      toast.success(`User ${user.is_active ? 'deactivated' : 'activated'}`);
      fetchUsers();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Filter users
  const filteredUsers = users.filter(user => 
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="admin-dashboard" data-testid="admin-dashboard">
      {/* Header */}
      <div className="admin-header">
        <div className="admin-header-left">
          <button className="back-btn" onClick={onBack} data-testid="admin-back-btn">
            <ChevronLeft size={20} />
            Back to App
          </button>
          <div className="admin-title">
            <Shield size={28} />
            <div>
              <h1>User Management</h1>
              <p>Manage users, tiers, and permissions</p>
            </div>
          </div>
        </div>
        
        <button 
          className="create-user-btn"
          onClick={() => setShowCreateModal(true)}
          data-testid="create-user-btn"
        >
          <UserPlus size={18} />
          Add User
        </button>
      </div>

      {/* Search Bar */}
      <div className="admin-toolbar">
        <div className="search-wrapper">
          <Search size={18} className="search-icon" />
          <input
            type="text"
            placeholder="Search users by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="user-search"
          />
          {searchTerm && (
            <button className="clear-search" onClick={() => setSearchTerm('')}>
              <X size={16} />
            </button>
          )}
        </div>
        <div className="user-count">
          {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Users List */}
      {loading ? (
        <div className="loading-state">Loading users...</div>
      ) : (
        <div className="users-list" data-testid="users-list">
          {filteredUsers.map(user => (
            <div key={user.id} className="user-card" data-testid={`user-card-${user.id}`}>
              {/* Avatar */}
              <div 
                className="user-avatar"
                style={{ backgroundColor: getAvatarColor(user.name) }}
              >
                {user.name.charAt(0).toUpperCase()}
              </div>

              {/* User Info */}
              <div className="user-info">
                <div className="user-name">{user.name}</div>
                <div className="user-email">{user.email}</div>
              </div>

              {/* Zodiac */}
              <div className="user-field zodiac-field">
                {user.zodiac ? (
                  <span className="zodiac-badge">
                    {user.zodiac.chinese} {user.zodiac.english}
                  </span>
                ) : (
                  <span className="not-set">-</span>
                )}
              </div>

              {/* Tier */}
              <div className="user-field">
                <span className={`tier-badge ${user.tier?.toLowerCase()}`}>
                  {user.role === 'admin' ? (
                    <><Crown size={12} /> Admin</>
                  ) : (
                    user.tier || 'Beginner'
                  )}
                </span>
              </div>

              {/* Expiry */}
              <div className="user-field expiry-field">
                {user.expiry_date ? formatDate(user.expiry_date) : 'Not set'}
              </div>

              {/* Created Date */}
              <div className="user-field date-field">
                {formatDate(user.created_at)}
              </div>

              {/* Actions */}
              <div className="user-actions">
                <button 
                  className="action-btn edit"
                  onClick={() => {
                    setSelectedUser(user);
                    setShowEditModal(true);
                  }}
                  title="Edit user"
                  data-testid={`edit-user-${user.id}`}
                >
                  <Edit2 size={16} />
                </button>
                <button 
                  className={`action-btn status ${user.is_active ? 'active' : 'inactive'}`}
                  onClick={() => handleToggleStatus(user)}
                  title={user.is_active ? 'Deactivate user' : 'Activate user'}
                  data-testid={`toggle-status-${user.id}`}
                >
                  {user.is_active ? <Eye size={16} /> : <Lock size={16} />}
                </button>
                <button 
                  className="action-btn permissions"
                  onClick={() => {
                    setSelectedUser(user);
                    setShowEditModal(true);
                  }}
                  title="Manage permissions"
                  data-testid={`permissions-${user.id}`}
                >
                  <Users size={16} />
                </button>
                <button 
                  className="action-btn delete"
                  onClick={() => handleDeleteUser(user.id, user.name)}
                  title="Delete user"
                  disabled={user.id === currentUser?.id}
                  data-testid={`delete-user-${user.id}`}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}

          {filteredUsers.length === 0 && (
            <div className="empty-state">
              <Users size={48} />
              <p>No users found</p>
            </div>
          )}
        </div>
      )}

      {/* Create User Modal */}
      {showCreateModal && (
        <UserModal
          title="Add New User"
          onClose={() => setShowCreateModal(false)}
          onSave={handleCreateUser}
        />
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <UserModal
          title="Edit User"
          user={selectedUser}
          onClose={() => {
            setShowEditModal(false);
            setSelectedUser(null);
          }}
          onSave={(data) => handleUpdateUser(selectedUser.id, data)}
        />
      )}
    </div>
  );
}

// User Modal Component
function UserModal({ title, user, onClose, onSave }) {
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    password: '',
    date_of_birth: user?.date_of_birth || '',
    gender: user?.gender || '',
    role: user?.role || 'user',
    tier: user?.tier || 'Beginner',
    expiry_date: user?.expiry_date || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    // Build update payload (exclude empty password for updates)
    const payload = { ...formData };
    if (user && !payload.password) {
      delete payload.password;
    }
    
    await onSave(payload);
    setSaving(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{title}</h2>
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-row">
            <div className="form-group">
              <label>Full Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                data-testid="modal-name"
              />
            </div>
            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                data-testid="modal-email"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>{user ? 'New Password (leave blank to keep)' : 'Password *'}</label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                required={!user}
                minLength={6}
                placeholder={user ? '••••••••' : ''}
                data-testid="modal-password"
              />
            </div>
            <div className="form-group">
              <label>Date of Birth</label>
              <input
                type="date"
                value={formData.date_of_birth}
                onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})}
                data-testid="modal-dob"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Gender</label>
              <select
                value={formData.gender}
                onChange={(e) => setFormData({...formData, gender: e.target.value})}
                data-testid="modal-gender"
              >
                <option value="">Select</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
            <div className="form-group">
              <label>Role</label>
              <select
                value={formData.role}
                onChange={(e) => setFormData({...formData, role: e.target.value})}
                data-testid="modal-role"
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Tier</label>
              <select
                value={formData.tier}
                onChange={(e) => setFormData({...formData, tier: e.target.value})}
                data-testid="modal-tier"
              >
                <option value="Beginner">Beginner</option>
                <option value="PRO">PRO</option>
              </select>
            </div>
            <div className="form-group">
              <label>Expiry Date</label>
              <input
                type="date"
                value={formData.expiry_date}
                onChange={(e) => setFormData({...formData, expiry_date: e.target.value})}
                data-testid="modal-expiry"
              />
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-save" disabled={saving}>
              <Save size={16} />
              {saving ? 'Saving...' : 'Save User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Helper function for avatar colors
function getAvatarColor(name) {
  const colors = [
    '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', 
    '#ec4899', '#ef4444', '#06b6d4', '#84cc16'
  ];
  const index = name.charCodeAt(0) % colors.length;
  return colors[index];
}

export default AdminDashboard;
